import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Users, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PersonPhoto = ({ photoFileId, name }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      if (!photoFileId) return;
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${BACKEND_URL}/api/files/${photoFileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          setPhotoUrl(objectUrl);
        }
      } catch { /* silencieux */ }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photoFileId]);

  if (photoUrl) {
    return (
      <>
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="cursor-zoom-in transition-transform hover:scale-105"
          title="Cliquer pour agrandir"
          data-testid={`zoom-photo-${name}`}
        >
          <img
            src={photoUrl}
            alt={name}
            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-white dark:border-slate-700 shadow-md"
          />
        </button>
        <Dialog open={zoomed} onOpenChange={setZoomed}>
          <DialogContent className="max-w-sm">
            <div className="flex flex-col items-center gap-3 pt-2">
              <img
                src={photoUrl}
                alt={name}
                className="w-64 h-64 rounded-full object-cover border-4 border-white dark:border-slate-700 shadow-lg"
              />
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{name}</p>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-700 flex items-center justify-center text-white font-bold shadow-md border-2 border-white dark:border-slate-700">
      {name?.charAt(0)?.toUpperCase() || '?'}
    </div>
  );
};

// Les noms sont enregistrés au format "NOM Prénom" (convention de l'inscription :
// nom en majuscules en premier). On sépare donc le préfixe de mots tout en
// majuscules (le nom) du reste (le prénom) pour les afficher sur deux lignes.
const splitNomPrenom = (fullName) => {
  if (!fullName) return { nom: '', prenom: '' };
  const parts = fullName.trim().split(/\s+/);
  let splitIdx = 0;
  while (
    splitIdx < parts.length &&
    parts[splitIdx] === parts[splitIdx].toLocaleUpperCase('fr-FR') &&
    /[A-ZÀ-Ÿ]/.test(parts[splitIdx])
  ) {
    splitIdx++;
  }
  if (splitIdx === 0) splitIdx = 1;
  return {
    nom: parts.slice(0, splitIdx).join(' '),
    prenom: parts.slice(splitIdx).join(' ')
  };
};

const TreeNode = ({ person }) => {
  const { nom, prenom } = splitNomPrenom(person.name);
  return (
    <div className="flex flex-col items-center gap-2 w-20 sm:w-28 text-center">
      <PersonPhoto photoFileId={person.photo_file_id} name={person.name} />
      <div>
        <p className="text-xs font-medium text-slate-800 dark:text-slate-100 leading-tight">{nom}</p>
        {prenom && (
          <p className="text-xs font-medium text-slate-800 dark:text-slate-100 leading-tight">{prenom}</p>
        )}
        {person.job_title && (
          <p className="text-[10px] text-indigo-500 dark:text-indigo-400 leading-tight mt-0.5">{person.job_title}</p>
        )}
      </div>
    </div>
  );
};

const chunkPeople = (people, size) => {
  const chunks = [];
  for (let i = 0; i < people.length; i += size) {
    chunks.push(people.slice(i, i + size));
  }
  return chunks;
};

// Largeur d'une carte (photo + marges) selon le point de rupture actuel,
// pour calculer combien tiennent par ligne dans le conteneur disponible
// (w-20/px-2 sous 640px, w-28/px-4 au-dessus - voir TreeNode ci-dessus).
const getNodeWidth = () => (window.innerWidth >= 640 ? 112 + 2 * 16 : 80 + 2 * 8);

// Nombre de personnes par ligne, recalculé à chaque redimensionnement de la
// fenêtre pour s'adapter à la taille de l'écran plutôt qu'un nombre fixe.
const useColumnsPerRow = () => {
  const containerRef = useRef(null);
  const [columns, setColumns] = useState(9);

  useEffect(() => {
    const recompute = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth || window.innerWidth;
      setColumns(Math.max(1, Math.floor(width / getNodeWidth())));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  return [containerRef, columns];
};

const SiteTree = ({ directionTiers, chefPostes, agents }) => {
  const lineColor = 'bg-slate-300 dark:bg-slate-600';
  const [containerRef, columnsPerRow] = useColumnsPerRow();

  // La Direction peut compter plusieurs niveaux (ex: PDG au-dessus du
  // Directeur) : chaque sous-niveau devient un étage distinct de l'arbre,
  // suivi de Chef de Poste puis Agents.
  const tiers = [
    ...directionTiers.map((people, i) => ({ key: `direction-${i}`, label: i === 0 ? 'Direction' : null, people, isDirection: true })),
    { key: 'chef_poste', label: 'Chef de Poste', people: chefPostes },
    { key: 'agents', label: 'Agents', people: agents },
  ].filter(t => t.people.length > 0);

  if (tiers.length === 0) {
    return <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">Aucun membre affecté à ce site pour le moment</p>;
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center py-4 overflow-x-auto">
      {tiers.map((tier, tierIdx) => (
        <React.Fragment key={tier.key}>
          {tierIdx > 0 && <div className={`w-0.5 h-6 ${lineColor}`} />}
          {tier.label && (
            <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${tier.isDirection ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}>
              {tier.label}
            </p>
          )}
          {chunkPeople(tier.people, columnsPerRow).map((row, rowIdx) => (
            <React.Fragment key={rowIdx}>
              {rowIdx > 0 && <div className={`w-0.5 h-4 ${lineColor}`} />}
              <div className="flex">
                {row.map((p, idx) => (
                  <div key={p.id} className="flex flex-col items-center px-2 sm:px-4 relative">
                    {row.length > 1 && tierIdx > 0 && (
                      <div
                        className={`absolute top-0 h-0.5 ${lineColor}`}
                        style={{
                          left: idx === 0 ? '50%' : 0,
                          right: idx === row.length - 1 ? '50%' : 0
                        }}
                      />
                    )}
                    {tierIdx > 0 && <div className={`w-0.5 h-6 ${lineColor}`} />}
                    <TreeNode person={p} />
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

const Trombinoscope = ({ user, onLogout }) => {
  const [siteTeams, setSiteTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/trombinoscope`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSiteTeams(await res.json());
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="trombinoscope">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Organigramme Action Sécurité
          </h1>
          <p className="text-gray-600 dark:text-slate-300">L'équipe présente sur votre site</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : siteTeams.length === 0 ? (
          <Card className="glass-effect">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-300">Aucune équipe à afficher pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          siteTeams.map((team) => (
            <Card key={team.site_id} className="glass-effect">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-slate-500" />
                  {team.site_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SiteTree directionTiers={team.direction_tiers || []} chefPostes={team.chef_postes} agents={team.agents} />
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </Layout>
  );
};

export default Trombinoscope;
